import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import ReactMapGl, {
  Source,
  Layer,
  FlyToInterpolator,
  ViewportProps,
  Popup,
  Marker,
} from 'react-map-gl';
import { useSelector, useDispatch } from 'react-redux';
import { Typography } from '@material-ui/core';
import { MdLocationOn } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/auth';
import CustomMarker from '../CustomMarker';
import OrderDetailsPopup from '../../utils/OrderDetailsPopup';

import { IState } from '../../../store';
import { IOrderDetailed } from '../../../types/orders';
import {
  IFormattedGeojson,
  ILayersData,
  ISelectedFeature,
} from '../../../types/layers';
import { splitGeojson } from '../../../utils/formatGeojson';

import { useStyles } from './styles';
import CreateOrderPopup from '../../utils/CreateOrderPopup';
import CreateOrderModal from '../../utils/CreateOrderModal';
import api from '../../../services/api';
import { IOrdersInput } from '../../../store/modules/orders/types';
import { getOrdersRequest } from '../../../store/modules/orders/actions';

interface IMarker {
  latitude: number;
  longitude: number;
  region: string;
  road: string;
  unit_id: string;
}

interface IBasemapsProps {
  isCreatingOrder: boolean;
  orders?: IOrderDetailed[];
  showOrders?: boolean;
  showRoads?: boolean;
  showRegions?: boolean;
  onDoneCreateOrder?: () => void;
  selectedFeatureName?: (selected: ISelectedFeature) => void;
}

export const INITIAL_SELECTED: ISelectedFeature = {
  id: 0,
  type: '',
  name: '',
  geom: '',
  unit: '',
  unit_id: '',
  lng: 0,
  lat: 0,
};

const Basemap = React.forwardRef<any, IBasemapsProps>(
  (
    {
      isCreatingOrder,
      orders,
      showOrders = true,
      showRoads = true,
      showRegions = true,
      onDoneCreateOrder,
      selectedFeatureName,
      children,
    },
    ref,
  ) => {
    const { user, jwt, selectedUnit } = useAuth();
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [mapstyle] = useState<string | undefined>(
      process.env.REACT_APP_MAPBOX_STYLE,
    );
    const [viewport, setViewport] = useState<ViewportProps>({
      longitude: user.units[0].lng,
      latitude: user.units[0].lat,
      zoom: 14,
      bearing: 0,
      pitch: 0,
    });
    const [selectedFeature, setSelectedFeature] = useState<ISelectedFeature>(
      INITIAL_SELECTED,
    );
    const [layerReady, setLayerReady] = useState<boolean>(false);
    const [showLayerInfo, setShowLayerInfo] = useState<boolean>(false);
    const [interactiveLayers, setInteractiveLayers] = useState<string[]>([]);
    const regionLayer = useSelector<IState, ILayersData[]>(
      appState => appState.layers.regions,
    );
    const roadLayer = useSelector<IState, ILayersData[]>(
      appState => appState.layers.roads,
    );
    const [regionGeojson, setRegionGeojson] = useState<IFormattedGeojson[]>([]);
    const [roadGeojson, setRoadGeojson] = useState<IFormattedGeojson[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<IOrderDetailed | null>(
      null,
    );
    const [filteredOrders, setFilteredOrders] = useState<IOrderDetailed[]>([]);
    const [newOrderMarker, setNewOrderMarker] = useState<IMarker | null>(null);
    const [openNewOrderPopup, setOpenNewOrderPopup] = useState(false);
    const [openCreateOrderDialog, setOpenCreateOrderDialog] = useState(false);
    const [isDeletingOrder, setIsDeletingOrder] = useState(false);

    const handleClickFeatures = useCallback(
      (event: any) => {
        const { features } = event;
        const clickedFeature = features && features[0];

        if (isCreatingOrder && clickedFeature) {
          // eslint-disable-next-line no-underscore-dangle
          setNewOrderMarker({
            unit_id: clickedFeature.properties.unit_id,
            latitude: event.lngLat[1],
            longitude: event.lngLat[0],
            // eslint-disable-next-line no-underscore-dangle
            region:
              clickedFeature.layer.type === 'line'
                ? // eslint-disable-next-line no-underscore-dangle
                  clickedFeature._vectorTileFeature.properties.geom
                : clickedFeature.properties.name,
            road:
              clickedFeature.layer.type === 'line'
                ? clickedFeature.properties.name
                : '-',
          });
          setOpenNewOrderPopup(true);
          return;
        }

        if (!isDeletingOrder) {
          if (clickedFeature) {
            const selected = {
              id: clickedFeature.properties.id,
              type: clickedFeature.layer.type,
              name: clickedFeature.properties.name,
              geom: clickedFeature.properties.geom,
              unit: clickedFeature.properties.unit,
              unit_id: clickedFeature.properties.unit_id,
              lng: event.lngLat[0],
              lat: event.lngLat[1],
            };
            setSelectedFeature(selected);
            if (selectedFeatureName) {
              selectedFeatureName(selected);
            }
            setViewport({
              longitude: event.lngLat[0],
              latitude: event.lngLat[1],
              zoom: 14,
              transitionInterpolator: new FlyToInterpolator({ speed: 1.2 }),
              transitionDuration: 500,
            });
            setSelectedOrder(null);
            setShowLayerInfo(true);
          } else {
            setSelectedFeature(INITIAL_SELECTED);
            setSelectedOrder(null);
            if (selectedFeatureName) {
              selectedFeatureName(INITIAL_SELECTED);
            }
          }
        }
      },
      [isCreatingOrder, selectedFeatureName, isDeletingOrder],
    );

    const handleClickOrder = useCallback(
      (order: IOrderDetailed) => {
        if (!selectedOrder) {
          setSelectedOrder(order);
          setShowLayerInfo(false);
        } else if (!isDeletingOrder) {
          setSelectedOrder(null);
        }
      },
      [selectedOrder, isDeletingOrder],
    );

    const handleClickClosePopup = (): void => {
      setTimeout(() => {
        setOpenNewOrderPopup(false);
      }, 400);
    };

    const handleDoneCreatingOrder = useCallback(() => {
      setNewOrderMarker(null);
      setOpenNewOrderPopup(false);
      if (onDoneCreateOrder) {
        onDoneCreateOrder();
      }
    }, [onDoneCreateOrder]);

    const handleCloseOrderDetailsPopup = (): void => {
      setSelectedOrder(null);
      setIsDeletingOrder(false);
    };
    const handleCancelDeleteOrder = (): void => {
      setSelectedOrder(null);
      setTimeout(() => {
        setIsDeletingOrder(false);
      }, 400);
    };

    const handleUpdateOrders = useCallback(
      (input: IOrdersInput) => {
        dispatch(getOrdersRequest(input));
      },
      [dispatch],
    );

    const handleDeleteOrder = useCallback(
      async (orderId: string): Promise<void> => {
        const reason = null;
        try {
          await api.put(
            `orders/status/change/${orderId}/EXCLUIDA/${user.id}`,
            reason,
            {
              headers: {
                'x-access-token': jwt,
              },
            },
          );
          handleCloseOrderDetailsPopup();
          dispatch(
            getOrdersRequest({
              user,
              jwt,
              status: 'ABERTA,INCOMPLETA,FINALIZADA',
            }),
          );
          toast.success(t('screens:orders.orderDeleteSuccess'));
        } catch {
          toast.error(t('screens:orders.orderDeleteError'));
        }
      },
      [t, user, jwt, dispatch],
    );

    const getCursor = (action: any): string => {
      if (isCreatingOrder) {
        return 'crosshair';
      }
      if (action.isDragging) {
        return 'grabbing';
      }
      if (action.isHovering) {
        return 'pointer';
      }
      return 'default';
    };

    // Expose clearFeatures to other components
    useImperativeHandle(ref, () => ({
      clearFeatures: () => {
        setSelectedFeature(INITIAL_SELECTED);
        setSelectedOrder(null);
      },
    }));

    // Create interactive layers array
    useEffect(() => {
      if (regionLayer && roadLayer) {
        if (regionLayer.length > 0 && roadLayer.length > 0) {
          const layers: string[] = [];
          const regionsGeojsons = splitGeojson(regionLayer);
          const roadsGeojsons = splitGeojson(roadLayer);
          if (selectedUnit.id === 'itabira') {
            regionsGeojsons.map((region: any) =>
              layers.push(
                `${region.name}_${region.properties.id}_${region.properties.unit_id}_layer`,
              ),
            );
            roadsGeojsons.map((road: any) =>
              layers.push(
                `${road.name}_${road.properties.id}_${road.properties.unit_id}_layer`,
              ),
            );
            setRegionGeojson(regionsGeojsons);
            setRoadGeojson(roadsGeojsons);
            setInteractiveLayers(layers);
            setLayerReady(true);
            return;
          }
          const filteredRegionsGeojsons = regionsGeojsons.filter(
            (geo: any) => geo.properties.unit_id === selectedUnit.id,
          );
          const filteredRoadsGeojsons = roadsGeojsons.filter(
            (geo: any) => geo.properties.unit_id === selectedUnit.id,
          );
          filteredRegionsGeojsons.map((region: any) =>
            layers.push(
              `${region.name}_${region.properties.id}_${region.properties.unit_id}_layer`,
            ),
          );
          filteredRoadsGeojsons.map((road: any) =>
            layers.push(
              `${road.name}_${road.properties.id}_${road.properties.unit_id}_layer`,
            ),
          );
          setRegionGeojson(filteredRegionsGeojsons);
          setRoadGeojson(filteredRoadsGeojsons);
          setInteractiveLayers(layers);
          setLayerReady(true);
        }
      }
    }, [regionLayer, roadLayer, selectedUnit]);

    // Update viewport
    useEffect(() => {
      setViewport({
        longitude: selectedUnit.lng,
        latitude: selectedUnit.lat,
        zoom: selectedUnit.id === 'itabira' ? 11.45 : 14,
        transitionInterpolator: new FlyToInterpolator({ speed: 1.2 }),
        transitionDuration: 500,
      });
    }, [selectedUnit]);

    // Remove new order marker when finish creating order
    useEffect(() => {
      if (!isCreatingOrder) {
        setNewOrderMarker(null);
      }
    }, [isCreatingOrder]);

    // Filter orders by selected feature
    useEffect(() => {
      if (orders) {
        if (selectedFeature.name !== '') {
          if (selectedFeature.type === 'fill') {
            const select = orders.filter(
              order => order.region === selectedFeature.name,
            );
            setFilteredOrders(select);
          } else if (selectedFeature.type === 'line') {
            const select = orders.filter(
              order => String(order.road) === String(selectedFeature.name),
            );
            setFilteredOrders(select);
          }
        } else {
          setFilteredOrders(orders);
        }
      }
    }, [selectedFeature, orders, selectedUnit]);

    return (
      <ReactMapGl
        {...viewport}
        maxZoom={20}
        mapStyle={mapstyle}
        mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
        width="100%"
        height="100%"
        onViewportChange={(newViewport: any) => {
          setViewport({ ...newViewport });
        }}
        onClick={handleClickFeatures}
        interactiveLayerIds={interactiveLayers}
        getCursor={action => getCursor(action)}
      >
        {newOrderMarker && (
          <Marker
            latitude={newOrderMarker.latitude}
            longitude={newOrderMarker.longitude}
            offsetLeft={-16}
            offsetTop={-32}
          >
            <MdLocationOn
              size={32}
              color="#7030A0"
              onClick={() => setOpenNewOrderPopup(true)}
            />
          </Marker>
        )}
        {showOrders && filteredOrders.length > 0
          ? filteredOrders.map(order => (
              <CustomMarker
                key={order.id}
                latitude={order.lat}
                longitude={order.lng}
                iconPriority={order.priority}
                iconStatus={order.status}
                type="filled"
                onClick={() => handleClickOrder(order)}
              />
            ))
          : showOrders &&
            orders &&
            orders.map(order => (
              <CustomMarker
                key={order.id}
                latitude={order.lat}
                longitude={order.lng}
                iconPriority={order.priority}
                iconStatus={order.status}
                type="filled"
                onClick={() => handleClickOrder(order)}
              />
            ))}
        {showRegions &&
          layerReady &&
          regionGeojson.map((geojson: any) => (
            <Source
              key={`${geojson.name}_${geojson.properties.id}_${geojson.properties.unit_id}_source`}
              type="geojson"
              data={geojson}
            >
              <Layer
                id={`${geojson.name}_${geojson.properties.id}_${geojson.properties.unit_id}_layer`}
                type="fill"
                paint={{
                  'fill-color':
                    selectedFeature.name === geojson.name
                      ? 'rgba(136, 73, 143, 0.7)'
                      : 'rgba(121, 173, 220, 0.5)',
                  'fill-outline-color': 'rgba(0, 0, 0, 0.7)',
                }}
              />
            </Source>
          ))}
        {showRoads &&
          layerReady &&
          roadGeojson.map((geojson: any) => (
            <Source
              key={`${geojson.name}_${geojson.properties.id}_${geojson.properties.unit_id}_source`}
              type="geojson"
              data={geojson}
            >
              <Layer
                id={`${geojson.name}_${geojson.properties.id}_${geojson.properties.unit_id}_layer`}
                type="line"
                paint={{
                  'line-color':
                    selectedFeature.name === geojson.name
                      ? 'rgba(136, 73, 143, 0.7)'
                      : 'rgba(8, 61, 119, 1)',
                  'line-width': 10,
                }}
              />
            </Source>
          ))}
        {showLayerInfo && (
          <Popup
            latitude={selectedFeature.lat}
            longitude={selectedFeature.lng}
            closeButton
            closeOnClick={false}
            onClose={() => setShowLayerInfo(false)}
            anchor="bottom"
            className={classes.popup}
          >
            <div className={classes.popupContent}>
              <Typography variant="subtitle1" className={classes.label}>
                {selectedFeature.type === 'line'
                  ? `${t('components:utils.road')}: ${selectedFeature.name}`
                  : `${t('components:utils.region')}: ${selectedFeature.name}`}
              </Typography>
            </div>
          </Popup>
        )}
        {selectedOrder && (
          <Popup
            latitude={selectedOrder.lat}
            longitude={selectedOrder.lng}
            closeButton
            closeOnClick={false}
            onClose={() => handleCloseOrderDetailsPopup()}
            anchor="bottom"
            className={classes.popup}
          >
            <OrderDetailsPopup
              order={selectedOrder}
              isDeleting={(status: any) => setIsDeletingOrder(status)}
              onCancelDelete={() => handleCancelDeleteOrder()}
              onConfirmDelete={() => handleDeleteOrder(selectedOrder.id)}
            />
          </Popup>
        )}
        {isCreatingOrder && newOrderMarker && openNewOrderPopup && (
          <Popup
            latitude={newOrderMarker.latitude}
            longitude={newOrderMarker.longitude}
            closeButton={false}
            closeOnClick={false}
            anchor="bottom"
            className={classes.popup}
            offsetTop={-16}
            captureClick
            captureDoubleClick
          >
            <CreateOrderPopup
              region={newOrderMarker.region}
              road={newOrderMarker.road}
              onClose={handleClickClosePopup}
              onConfirm={() => setOpenCreateOrderDialog(true)}
            />
          </Popup>
        )}
        {newOrderMarker && (
          <CreateOrderModal
            open={openCreateOrderDialog}
            unitId={newOrderMarker.unit_id}
            lat={newOrderMarker.latitude}
            lng={newOrderMarker.longitude}
            region={newOrderMarker.region}
            road={newOrderMarker.road}
            onCancel={() => setOpenCreateOrderDialog(false)}
            onClose={() => setOpenCreateOrderDialog(false)}
            onDone={() => handleDoneCreatingOrder()}
          />
        )}
        {children}
      </ReactMapGl>
    );
  },
);

export default Basemap;
